// ===========================================
// Event Venue React Query Hooks (Admin)
// ===========================================
// Reusable external event venues (concert halls etc.). All admin/events-scoped.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  eventApi,
  CreateEventVenueData,
  UpdateEventVenueData,
} from '../api/events.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const eventVenueKeys = {
  all: ['eventVenues'] as const,
  lists: () => [...eventVenueKeys.all, 'list'] as const,
  detail: (id: string) => [...eventVenueKeys.all, 'detail', id] as const,
};

type ApiError = Error & { response?: { data?: { message?: string } } };

// ===========================================
// QUERY HOOKS
// ===========================================

/**
 * List all reusable venues for the school (alphabetical).
 */
export function useEventVenues() {
  return useQuery({
    queryKey: eventVenueKeys.lists(),
    queryFn: () => eventApi.listVenues(),
  });
}

/**
 * Get a single reusable venue by id.
 */
export function useEventVenue(venueId: string) {
  return useQuery({
    queryKey: eventVenueKeys.detail(venueId),
    queryFn: () => eventApi.getVenue(venueId),
    enabled: !!venueId,
  });
}

// ===========================================
// MUTATION HOOKS
// ===========================================

/**
 * Create a reusable venue. Returns the created venue so callers (e.g. the
 * inline NewVenueForm) can immediately select it.
 */
export function useCreateEventVenue() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreateEventVenueData) => eventApi.createVenue(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventVenueKeys.lists() });
      enqueueSnackbar('Venue saved', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to save venue';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Update a reusable venue.
 */
export function useUpdateEventVenue() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEventVenueData }) =>
      eventApi.updateVenue(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: eventVenueKeys.lists() });
      queryClient.invalidateQueries({ queryKey: eventVenueKeys.detail(id) });
      enqueueSnackbar('Venue updated', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to update venue';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Delete a reusable venue. Surfaces the backend 409 ("in use by N events")
 * message verbatim so the admin knows why it can't be removed.
 */
export function useDeleteEventVenue() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (venueId: string) => eventApi.deleteVenue(venueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventVenueKeys.lists() });
      enqueueSnackbar('Venue deleted', { variant: 'info' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to delete venue';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}
